import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Badge } from '@quebon/ui/components/badge'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { cn } from '@quebon/ui/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@quebon/ui/components/dialog'
import { lcCoachingApi } from '@/api/lcCoaching'
import { useEnsureTest, useTestDetail } from '@/hooks/coaching/useTests'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { getApiErrorMessage } from '@/utils/apiError'
import type { TestSelection, LcTestDetailView } from '@/types/coaching'
import { PartialCreditGradeToggle } from '../component-details/parts/PartialCreditGradeToggle'
import { GradingCorrectAnswer } from '../grading-sheet/GradingCorrectAnswer'
import { MarkAllCorrectButton } from '../MarkAllCorrectButton'
import { EMPTY_SCOPE, hasHandheldDrafts, type HandheldDraftStore } from './handheldDraftStore'

interface Props {
  presentation?: 'mobile' | 'workspace'
  readOnly?: boolean
  learnerId: number
  selection: TestSelection
  testId?: number
  store: HandheldDraftStore
  onChanged: () => Promise<unknown>
}

export function HandheldTestGrading({ presentation = 'mobile', readOnly = false, learnerId, selection, testId: override, store, onChanged }: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const user = useAuthStore(s => s.user)
  const workspace = presentation === 'workspace'
  const canEdit = !readOnly && hasLcPermission(user ?? undefined, 'COACHING')
  // The summary may advance after completion while this detail still owns the opened round.
  const [openedTestId, setOpenedTestId] = useState(override)
  useEffect(() => { if (override != null) setOpenedTestId(override) }, [override])
  const selectedTestId = override ?? openedTestId
  const ensure = useEnsureTest(learnerId, selectedTestId == null ? selection : null, { enabled: canEdit })
  const testId = selectedTestId ?? ensure.data?.testId
  useEffect(() => { if (testId != null) setOpenedTestId(testId) }, [testId])
  const detail = useTestDetail(learnerId, testId)
  const qc = useQueryClient()
  const scope = `test:${learnerId}:${testId ?? 'pending'}`
  const state = useStore(store, s => s.scopes[scope] ?? EMPTY_SCOPE)
  const saving = useStore(store, s => s.saving)
  const dirty = useStore(store, s => Object.values(s.scopes).some(v => Object.keys(v.drafts).length > 0))
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const data = detail.data
  const items = [...(data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
  const pending = Object.keys(state.drafts).length
  const blocked = saving || !canEdit || !data || detail.isError
  const markAllCorrect = () => {
    if (blocked || testId == null || items.length === 0 || store.getState().saving) return
    for (const item of items) {
      const current = store.getState().scopes[scope] ?? EMPTY_SCOPE
      const baseline = current.acknowledged[item.quizId] ?? (item.gradedIsCorrect == null ? null : item.gradedIsCorrect ? 1 : 0)
      if (baseline === 1) {
        if (current.drafts[item.quizId] !== undefined) store.getState().setGrade(scope, item.quizId, baseline, 1)
        continue
      }
      // Individual controls toggle repeated values; bulk selection must stay selected.
      if (current.drafts[item.quizId] !== 1) store.getState().setGrade(scope, item.quizId, baseline, 1)
    }
  }
  const refresh = async () => {
    const [result] = await Promise.all([
      detail.refetch({ throwOnError: true }), onChanged(),
      qc.invalidateQueries({ queryKey: coachingKeys.tests(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.library(learnerId, 'all') }),
    ])
    if (result.data) store.getState().clearAcknowledged(scope)
  }
  const save = async () => {
    if (blocked || testId == null || !store.getState().beginSave()) return
    setError(null)
    try {
      const edits = Object.entries(store.getState().scopes[scope]?.drafts ?? {})
      for (const [quizId, grade] of edits) {
        if (!items.some(item => item.quizId === quizId)) {
          store.getState().failed(scope, quizId, t('handheldGrading.staleTarget'))
          continue
        }
        try {
          const updated = await lcCoachingApi.gradeTestItem(learnerId, testId, quizId, grade === 1, undefined, true)
          qc.setQueryData<LcTestDetailView>(coachingKeys.testDetail(learnerId, testId), updated)
          store.getState().saved(scope, quizId, grade)
        } catch (e) { store.getState().failed(scope, quizId, getApiErrorMessage(e, t)) }
      }
      await refresh()
    } catch (e) { setError(getApiErrorMessage(e, t)) }
    finally { store.getState().endSave() }
  }
  const complete = async () => {
    if (blocked || testId == null || hasHandheldDrafts(store) || !allGraded || !store.getState().beginSave()) return
    setError(null)
    try {
      const updated = await lcCoachingApi.completeTest(learnerId, testId)
      qc.setQueryData(coachingKeys.testDetail(learnerId, testId), updated)
      setConfirm(false)
      await refresh()
    } catch (e) { setError(getApiErrorMessage(e, t)) }
    finally { store.getState().endSave() }
  }
  const allGraded = items.length > 0 && items.every(item => item.gradedIsCorrect != null)
  const title = t(selection.kind === 'CHAPTER' ? 'test.chapterTitle' : 'handheldTests.lessonTest', { chapter: selection.chapterOrdinal })
  if (readOnly && testId == null) return <Alert><AlertDescription>{t('todayGrading.readOnlyTest')}</AlertDescription></Alert>
  if (ensure.isError || (detail.isError && !data)) return <Alert variant="destructive"><AlertDescription>{t('test.loadError')}<Button className="min-h-11" variant="outline" disabled={!canEdit && testId == null} onClick={() => { if (testId != null) void detail.refetch(); else if (canEdit) void ensure.refetch() }}>{t('roster.retry')}</Button></AlertDescription></Alert>
  if (!data) return <p role="status">{t('test.loading')}</p>
  const completeLabel = t(data.kind === 'CHAPTER' ? 'test.completeChapter' : 'test.completeCumulative')
  return <section className={cn('flex min-w-0 flex-col gap-4', !workspace && pending > 0 && 'pb-24')} aria-label={title} data-presentation={presentation}>
    <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-lg font-semibold">{workspace ? t('todayGrading.assessmentTitle') : title}</h2>{workspace ? <Badge variant="outline">{title}</Badge> : null}<Badge variant={data.status === 'COMPLETED' ? 'default' : 'outline'}>{t(data.status === 'COMPLETED' ? 'handheldTests.completed' : 'handheldTests.progress', { graded: items.filter(i => i.gradedIsCorrect != null).length, total: items.length })}</Badge>{canEdit && items.length > 0 ? <MarkAllCorrectButton disabled={blocked} onClick={markAllCorrect} /> : null}</div>
    {error ? <Alert variant="destructive"><AlertDescription>{error}<Button variant="outline" className="min-h-11" disabled={saving} onClick={() => { setError(null); void refresh().catch(e => setError(getApiErrorMessage(e, t))) }}>{t('roster.retry')}</Button></AlertDescription></Alert> : null}
    {items.length === 0 ? <p>{t('test.empty')}</p> : <ol className="flex flex-col gap-4">{items.map((item, index) => {
      const baseline = state.acknowledged[item.quizId] ?? (item.gradedIsCorrect == null ? null : item.gradedIsCorrect ? 1 : 0)
      const value = state.drafts[item.quizId] ?? baseline
      return <li key={item.quizId} className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4" aria-label={t('gradingMode.rowLabel', { no: index + 1 })}>
        <div className="flex items-center gap-2"><span>{t('gradingMode.rowLabel', { no: index + 1 })}</span>{item.quizId in state.drafts ? <Badge>{t('handheldGrading.unsavedRow')}</Badge> : null}</div>
        <div className="min-w-0 overflow-x-auto text-2xl font-bold text-destructive"><GradingCorrectAnswer correctAnswer={item.correctAnswer} questionSnapshot={item.questionSnapshot} mobile /></div>
        <PartialCreditGradeToggle allowPartial={false} value={value === 1 ? 1 : value === 0 ? 0 : null} disabled={blocked} onChange={grade => store.getState().setGrade(scope, item.quizId, baseline, grade)} />
        {state.failed[item.quizId] ? <p role="alert" className="text-sm text-destructive">{state.failed[item.quizId]}</p> : null}
      </li>
    })}</ol>}
    {Object.keys(state.drafts).some(id => !items.some(item => item.quizId === id)) ? <Alert variant="destructive"><AlertDescription>{t('handheldGrading.staleTarget')}<Button variant="outline" className="min-h-11" disabled={saving} onClick={() => Object.keys(state.drafts).filter(id => !items.some(item => item.quizId === id)).forEach(id => store.getState().setGrade(scope, id, null, null))}>{t('handheldGrading.discardUnavailable')}</Button></AlertDescription></Alert> : null}
    <Button className="min-h-11" disabled={blocked || dirty || !allGraded || data.status === 'COMPLETED'} onClick={() => setConfirm(true)}>{completeLabel}</Button>
    {pending > 0 ? <div className={cn('bottom-0 z-20 border-t bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))]', workspace ? 'sticky min-w-0' : 'fixed inset-x-0')} data-testid={workspace ? 'workspace-test-savebar' : undefined} aria-label={t('handheldGrading.saveBar')}><div className="mx-auto flex max-w-3xl items-center justify-between gap-2"><span role="status">{t('handheldGrading.unsaved', { count: pending })}</span><Button className="min-h-11" disabled={blocked} onClick={() => void save()}><Save data-icon="inline-start" />{t('handheldGrading.save', { count: pending })}</Button></div></div> : null}
    <Dialog open={confirm} onOpenChange={open => { if (!saving) setConfirm(open) }}><DialogContent showCloseButton={false}><DialogHeader><DialogTitle>{completeLabel}</DialogTitle><DialogDescription>{t('handheldTests.completeConfirm')}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" className="min-h-11" disabled={saving} onClick={() => setConfirm(false)}>{t('sessionControl.cancel')}</Button><Button className="min-h-11" disabled={blocked || dirty || !allGraded} onClick={() => void complete()}>{t('lessonComplete.confirmLabel')}</Button></DialogFooter></DialogContent></Dialog>
  </section>
}
